<template>
<v-container fluid>
    <v-card class="ma-sm-2" outlined>
        <v-progress-linear indeterminate color="yellow darken-2" v-if="loading"></v-progress-linear>
        <v-card-title class="d-flex">
            <v-btn icon @click="back()">
                <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            {{this.userInfo.name}}
        </v-card-title>
        <v-spacer></v-spacer>
        <group-view-filter 
            v-if="isShowGroupFilter" 
            @toCreateGroupOverview="toCreateGroupOverview($event)" 
            @toExport="toExport($event)" 
            :loading="loading" 
            :startDate="startDate" 
            :endDate="endDate" 
            :isExporting="isExporting" 
            :groupId="this.id" 
            :uid="this.uid"
            />
    </v-card>
    <chart-member-component-vue :chartInfo="chartWeightMember" v-if="!loading && !isExporting"/>
    <chart-member-component-vue :chartInfo="chartBMIMember" v-if="!loading && !isExporting" />
    <chart-member-component-vue :chartInfo="chartPressureMember" v-if="!loading && !isExporting" />
    <chart-member-component-vue :chartInfo="chartLDLMember" v-if="!loading && !isExporting" />
    <chart-member-component-vue :chartInfo="chartFBSMember" v-if="!loading && !isExporting" />
    <chart-member-component-vue :chartInfo="chartWorkoutMinsMember" v-if="!loading && !isExporting" />
    <chart-member-component-vue :chartInfo="chartPBWFMember" v-if="!loading && !isExporting" />
    <v-snackbar
      v-model="snackbar"
      multi-line
    >
      {{ text }}

      <template v-slot:actions>
        <v-btn
          color="red"
          variant="text"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
</v-container>
</template>

<script>
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import {
    mapGetters
} from "vuex";
import getUserInfoByUserId from '../data/getUserInfoByUserId';
import GroupViewFilter from "../components/GroupViewFilter.vue";
import bqGetMemberSummary from "../data/bqGetMemberSummary";
import ChartMemberComponentVue from "../components/ChartMemberComponent.vue";
import colors from 'vuetify/lib/util/colors';
import moment from "moment";
import bqExportData from "../data/bqExportData";
import formatData from "../utils/formatData";
export default {
    name: 'MemberView',
    metaInfo: {
        title: 'Member List'
    },
    components: {
        GroupViewFilter,
        ChartMemberComponentVue
    },
    data() {
        return {
            loading: false,
            isExporting: false,
            //set default date from last 3 month
            isShowGroupFilter: false,
            startDate: moment().subtract(3, 'month').format('YYYY-MM-DD'),
            endDate: moment().format('YYYY-MM-DD'),

            userInfo: {
                name: 'Loading..'
            },
            chartWeightMember: {
                noData: true,
                chartTitle: "Weight",
                title: 'Weight',
                toolbarShow: true,
                titleHide: true,
                color: 'indigo',
                series: [{
                    name: "Weight",
                    color: colors.indigo.lighten3,
                    data: []
                }],
                forceNiceScale: true,
                maxY: 80,
                score: 50,
            },
            chartBMIMember: {
                noData: true,
                chartTitle: "BMI",
                title: 'BMI',
                toolbarShow: true,
                titleHide: true,
                color: 'green',
                series: [{
                    name: "BMI",
                    color: colors.green.lighten3,
                    data: []
                }],
                maxY: 35,
                score: 23,
                tickAmount: 2,
                forceNiceScale: true,
                annotations: [{
                    y: 23,
                    borderColor: colors.green.lighten2,
                    label: {
                        borderColor: colors.green.darken4,
                        style: {
                            color: "#FFF",
                            background: colors.green.lighten2
                        },
                        text: '23',
                        position: 'left',
                        offsetX: -10,
                        offsetY: 8
                    }
                }]
            },
            chartPressureMember: {
                noData: true,
                chartTitle: "Pressure",
                title: 'Pressure',
                toolbarShow: true,
                titleHide: true,
                color: 'deepOrange',
                series: [{
                    name: "Upper",
                    color: colors.deepOrange.lighten3,
                    data: []
                }, {
                    name: "Lower",
                    color: colors.teal.lighten3,
                    data: []
                }],
                score: 135,
                maxY: 150,
                tickAmount: 4,
                forceNiceScale: true,
                annotations: [{
                    y: 135,
                    borderColor: colors.deepOrange.lighten2,
                    label: {
                        borderColor: colors.deepOrange.darken4,
                        style: {
                            color: "#FFF",
                            background: colors.deepOrange.lighten2
                        },
                        text: '135',
                        position: 'left',
                        offsetX: -10,
                        offsetY: 8
                    }
                }, {
                    y: 85,
                    borderColor: colors.teal.lighten2,
                    label: {
                        borderColor: colors.teal.darken4,
                        style: {
                            color: "#FFF",
                            background: colors.teal.lighten2
                        },
                        text: '85',
                        position: 'left',
                        offsetX: -10,
                        offsetY: 8
                    },
                }]
            },
            chartLDLMember: {
                noData: true,
                chartTitle: "LDL",
                title: 'LDL',
                toolbarShow: true,
                titleHide: true,
                color: 'blue',
                series: [{
                    name: "LDL",
                    color: colors.blue.lighten3,
                    data: []
                }],
                maxY: 150,
                score: 130,
                annotations: [{
                    y: 130,
                    borderColor: colors.blue.lighten2,
                    label: {
                        borderColor: colors.blue.darken4,
                        style: {
                            color: "#FFF",
                            background: colors.blue.lighten2
                        },
                        text: '130',
                        position: 'left',
                        offsetX: -10,
                        offsetY: 8
                    }
                }]
            },
            chartFBSMember: {
                noData: true,
                chartTitle: "FBS",
                title: 'FBS',
                toolbarShow: true,
                titleHide: true,
                color: 'pink',
                series: [{
                    name: "FBS",
                    color: colors.pink.lighten3,
                    data: []
                }],
                maxY: 150,
                score: 130,
                annotations: [{
                    y: 130,
                    borderColor: colors.pink.lighten2,
                    label: {
                        borderColor: colors.pink.darken4,
                        style: {
                            color: "#FFF",
                            background: colors.pink.lighten2
                        },
                        text: '130',
                        position: 'left',
                        offsetX: -10,
                        offsetY: 8
                    }
                }]
            },
            chartWorkoutMinsMember: {
                noData: true,
                chartTitle: "Workout",
                chartType: "bar",
                title: "Workout",
                toolbarShow: true,
                titleHide: true,
                color: "pink",
                series: [{
                    name: "Workout",
                    color: colors.purple.lighten3,
                    data: []
                }],
            },
            chartPBWFMember: {
                noData: true,
                chartTitle: "PBWF",
                chartType: "bar",
                title: "PBWF",
                toolbarShow: true,
                titleHide: true,
                color: "pink",
                series: [{
                    name: "PBWF",
                    color: colors.orange.lighten3,
                    data: []
                }],
            },
            snackbar: false,
            text: `Convert to Excel failed`,
        };
    },
    created() {
        var vm = this;
        vm.$store.state.appTitle = "Thaisook Web Admin | Member";
        firebase.auth().onAuthStateChanged((user) => {
            if (user) {
                vm.$store.dispatch("autoSignIn", user);
                this.loading = true;
                setTimeout(() => {
                    vm.initialize();
                }, "1000");
            } else {
                console.log("no user");
            }
        });

        //group id
        this.id = this.$route.params.id;
        //user id
        this.uid = this.$route.params.uid;
    },
    computed: {
        ...mapGetters([
            "loggedInUserId",
            "isAuthenticated",
            "isSuperAdmin",
            "isChallengeAdmin",
        ]),
    },
    methods: {
        initialize() {
            
            (async () => {
                let isAllowed = 1;
                // if( !this.isSuperAdmin ){
                //     const memberList = await getMemberInGroup(this.id);
                //     isAllowed = memberList.includes(this.loggedInUserId);
                //     if (isAllowed) {
                //         isAllowed = memberList.includes(this.uid);
                //     }
                // }
                if (isAllowed) {
                    this.userInfo = await getUserInfoByUserId(this.uid);
                    if (localStorage.getItem('startDate') && localStorage.getItem('endDate')) {
                        this.startDate = localStorage.getItem('startDate');

                        this.endDate= localStorage.getItem('endDate');
                        
                        this.toCreateGroupOverview( {uid: this.uid, startDate: this.startDate, endDate: this.endDate, isSubmit: true} );
                        this.isShowGroupFilter = true;
                    }
                    else{
                        this.isShowGroupFilter = true;
                    }
                    this.loading = false;
                } else {
                    this.$router.push(`/dashboard`);
                }
            })();
        },

        calculateMaxY(maxValue, maxY, point) {
            //compare with maxY
            if (maxValue > maxY) {
                maxY = (maxValue)*1 + point;
            }
            return maxY;
        },

        calculatePercentChanges(a, b) {
            //get max value
            // return (b-a)/a*100;
            let pc = formatData('number', (b-a)/a*100);
            if ( pc > 0 ){
                pc = `+${pc}`;
            }
            return pc;
        },

        goToDashboard() {
            this.$router.push(`/dashboard`);
        },
        merge(a, b, prop) {
            var reduced = [];
            for (var i = 0; i < a.length; i++) {
                var aitem = a[i];
                var found = false;
                for (var ii = 0; ii < b.length; ii++) {
                    if (aitem[prop] === b[ii][prop]) {
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    reduced.push(aitem);
                }
            }
            return reduced.concat(b);

        },
        back(){
            this.$router.go(-1);
        },
        toExport( obj ){
            (async () => {
                this.isExporting = true;
                const downloadUrl = await bqExportData(obj.groupId, obj.startDate, obj.endDate, obj.uid);
                if( downloadUrl ){
                    window.location = downloadUrl;
                    this.isExporting = false;
                }
                else{
                    this.snackbar = true;
                    this.isExporting = false;
                }
            })();
        },
        toCreateGroupOverview( obj ){
            this.loading = true;
            // this.uid = obj.uid;
            this.startDate = obj.startDate;
            this.endDate = obj.endDate;
            // this.groupInfo.isSubmit = obj.isSubmit;
            (async () => {
                this.wellnessData = await bqGetMemberSummary( this.uid, this.startDate, this.endDate );
                this.chartWeightMember.series[0].data = [];
                this.chartWeightMember.noData = true;
                if( this.wellnessData.weight.length ){
                    this.chartWeightMember.noData = false;
                    this.chartWeightMember.series[0].data = this.wellnessData.weight;
                    this.chartWeightMember.maxY = this.calculateMaxY(this.wellnessData.weightMaxY, this.chartWeightMember.maxY, 5);
                    this.chartWeightMember.chartSubTitle = this.calculatePercentChanges(this.wellnessData.weightStart, this.wellnessData.weightEnd) + '%';
                    this.chartWeightMember.title = this.chartWeightMember.chartTitle + ' (' + this.chartWeightMember.chartSubTitle + ')';
                }

                this.chartBMIMember.series[0].data = [] || 0;
                this.chartBMIMember.noData = true;
                if( this.wellnessData.bmi.length ){
                    this.chartBMIMember.noData = false;
                    this.chartBMIMember.series[0].data = this.wellnessData.bmi;
                    this.chartBMIMember.maxY = this.calculateMaxY(this.wellnessData.bmiMaxY, this.chartBMIMember.maxY, 5);
                    this.chartBMIMember.chartSubTitle = this.calculatePercentChanges(this.wellnessData.bmiStart, this.wellnessData.bmiEnd) + '%';
                    this.chartBMIMember.title = this.chartBMIMember.chartTitle + ' (' + this.chartBMIMember.chartSubTitle + ')';
                }

                this.chartLDLMember.series[0].data = [];
                this.chartLDLMember.noData = true;
                if( this.wellnessData.ldl.length ){
                    this.chartLDLMember.noData = false;
                    this.chartLDLMember.series[0].data = this.wellnessData.ldl;
                    this.chartLDLMember.maxY = this.calculateMaxY(this.wellnessData.ldlMaxY, this.chartLDLMember.maxY, 5);
                    this.chartLDLMember.chartSubTitle = this.calculatePercentChanges(this.wellnessData.ldlStart, this.wellnessData.ldlEnd) + '%';
                    this.chartLDLMember.title = this.chartLDLMember.chartTitle + ' (' + this.chartLDLMember.chartSubTitle + ')';
                }

                this.chartFBSMember.series[0].data = [];
                this.chartFBSMember.noData = true;
                if( this.wellnessData.fbs.length ){
                    this.chartFBSMember.noData = false;
                    this.chartFBSMember.series[0].data = this.wellnessData.fbs;
                    this.chartFBSMember.maxY = this.calculateMaxY(this.wellnessData.fbsMaxY, this.chartFBSMember.maxY, 5);
                    this.chartFBSMember.chartSubTitle = this.calculatePercentChanges(this.wellnessData.fbsStart, this.wellnessData.fbsEnd) + '%';
                    this.chartFBSMember.title = this.chartFBSMember.chartTitle + ' (' + this.chartFBSMember.chartSubTitle + ')';
                }

                this.chartPressureMember.series[0].data = [];
                this.chartPressureMember.series[1].data = [];
                this.chartPressureMember.noData = true;
                if( this.wellnessData.pressureUpper.length || this.wellnessData.pressureLower.length ){
                    this.chartPressureMember.noData = false;
                    this.chartPressureMember.series[0].data = this.wellnessData.pressureUpper;
                    this.chartPressureMember.series[1].data = this.wellnessData.pressureLower;
                    this.chartPressureMember.maxY = this.calculateMaxY(this.wellnessData.pressureMaxY, this.chartPressureMember.maxY, 5);
                    this.chartPressureMember.chartSubTitle = this.calculatePercentChanges(this.wellnessData.pressureUpperStart, this.wellnessData.pressureUpperEnd) + '%';
                    this.chartPressureMember.title = this.chartPressureMember.chartTitle + ' (' + this.chartPressureMember.chartSubTitle + ')';
                }

                this.chartWorkoutMinsMember.series[0].data = [];
                this.chartWorkoutMinsMember.noData = true;
                if( this.wellnessData.workoutMins.length ){
                    this.chartWorkoutMinsMember.noData = false;
                    this.chartWorkoutMinsMember.series[0].data = this.wellnessData.workoutMins;
                    this.chartWorkoutMinsMember.chartSubTitle = 'total ' + this.wellnessData.workoutMinsTotal + ' minutes';
                    this.chartWorkoutMinsMember.title = this.chartWorkoutMinsMember.chartTitle + ' (' + this.chartWorkoutMinsMember.chartSubTitle + ')';
                }

                this.chartPBWFMember.series[0].data = [];
                this.chartPBWFMember.noData = true;
                if( this.wellnessData.pbwf.length ){
                    this.chartPBWFMember.noData = false;
                    this.chartPBWFMember.series[0].data = this.wellnessData.pbwf;
                    this.chartPBWFMember.chartSubTitle = 'total ' + this.wellnessData.pbwfTotal + ' servings';
                    this.chartPBWFMember.title = this.chartPBWFMember.chartTitle + ' (' + this.chartPBWFMember.chartSubTitle + ')';
                }

                this.loading = false;
            })();

        }
    },
};
</script>